import React, { useEffect, useContext, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  tableLoading,
  formatPrice,
  getSystemSettings,
  toastObj,
  customStyle,
  validateItemsProperty
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useCustomHooks } from "../../../../../functions/Hooks";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useGeneralHooks } from "../../../../../hooks/generalHooks"

//pages & includes
import ManageSidebar from "../ManageSidebar";

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import { SettingsContext } from "../../../../../contexts/Settings";
import { FoodContext } from "../../../../../contexts/Food";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const FgInvoceAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams();
  const search = useLocation().search;
  const duplicate = new URLSearchParams(search).get('duplicate');
  const { roundOffStock, qtyAmountPoints } = useCustomHooks();
  const { piBackDate } = useGeneralHooks();
  //getting context values here
  let {
    loading,
    setLoading,
    branchForSearch,
    branchForListSearch,
    ingredientLimitCheck,
    getIngredientLimit,

    dataPaginating,
    setDataPaginating
  } = useContext(RestaurantContext);

  let { foodForPOS,getFoodItemsForPOS } = useContext(FoodContext)

  
  let { getSupplier, supplierForSearch, authUserInfo } = useContext(UserContext);
  const { generalSettings } = useContext(SettingsContext);
  
  // States hook here
  //new item
  let [newItem, setNewItem] = useState({
    branch: null,
    supplier: null,
    transportation: 0,
    invoice: "",
    ref_doc_identity: null,
    is_ref_no: 0,
    description: "",
    document:"",
    paymentType: "cash",
    items: [],
    paid: null,
    total: null,
    item_amount: 0,
    item_dis: 0,
    item_tax: 0,
    manual_invoice_no: "",
    credit_limit_days: "",
  });

  //new Row
  const [newRow,setNewRow]=useState([]);

  //edit purchase
  const [editPurchase, setEditPurchase] = useState(null);

  //datepicker
  const [purchaseDate, setPurchaseDate] = useState(new Date());

  //useEffect == componentDidMount()
  useEffect(() => {
    if(!supplierForSearch){
        getSupplier();
    }
    if(!foodForPOS){
        getFoodItemsForPOS();
    }
    if(params.id){
      getPurchasedItem();
    }
    if(!ingredientLimitCheck){
      getIngredientLimit()
    }
  }, []);

  const { fgDiscountFixed, fgTaxFixed, fgPurchaseStock } = useMemo(()=>{
    return {
      fgDiscountFixed: parseInt(getSystemSettings(generalSettings, "fg_discount")) === 1,
      fgTaxFixed: parseInt(getSystemSettings(generalSettings, "fg_tax")) === 1,
      fgPurchaseStock: parseInt(getSystemSettings(generalSettings, "fg_purchase_stock_qty")) === 1,
    } 
  },[generalSettings]);

  //Get Specific item for edit
  const getPurchasedItem = async () => {
    setLoading(true);
    const url = BASE_URL + "/settings/get-fg-purchase-items/" + parseInt(params.id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setEditPurchase(res.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching FG purchase:", error);
      setLoading(false);
    }
  };

  //Populate form when edit data is loaded
  useEffect(() => {
    if (editPurchase && editPurchase !== null) {
      let docDate = new Date(editPurchase[1].purchase_date);
      let branch = branchForListSearch[editPurchase[1].branch_id];
      let supplier = supplierForSearch?.find((item) => editPurchase[1].supplier_id === item.id);

      setNewItem({
        ...newItem,
        invoice: editPurchase[1].invoice_number,
        branch,
        ref_doc_identity: !duplicate && editPurchase[1].ref_doc_identity 
          ? { document_identity: editPurchase[1].ref_doc_identity } 
          : null,
        is_ref_no: 0,
        supplier,
        transportation: editPurchase[1].transportation,
        paymentType: editPurchase[1].payment_type,
        description: editPurchase[1]?.desc ?? "",
        credit_limit_days: editPurchase[1].credit_limit_days ?? "",
        manual_invoice_no: editPurchase[1].manual_invoice_no ?? "",
        document: editPurchase[1]?.fgp_document ?? "",
      });
      setPurchaseDate(docDate);
      
      let rowArr = [];
      if (editPurchase[0] && editPurchase[0].length > 0) {
        editPurchase[0].map((item) => {
          const maxLimit = ingredientLimitCheck?.fgs?.[editPurchase[1].branch_id]?.[item.item_id]?.max_limit;
          rowArr.push({
            item_id: item.item_id,
            item_name: item.item_name,
            finished_good_stock: item?.finished_good_stock,
            max_qty: branch?.max_qty_check_purchase && maxLimit ? (maxLimit ?? 0) - (Number(item?.actual_stock) || 0) : "",
            actual_stock: item?.actual_stock ?? "",
            qty: parseInt(item?.qty) || 0,
            rate: roundOffStock(item.rate),
            amount: roundOffStock(item.amount),
            discount_percent: roundOffStock(item.discount_percent),
            discount: roundOffStock(item.discount),
            tax_percent: roundOffStock(item.tax_percent),
            tax: roundOffStock(item.tax),
            advance_tax_percent: roundOffStock(item.advance_tax_percent) ?? 0,
            advance_tax: roundOffStock(item.advance_tax) ?? 0,
            total: roundOffStock(item.total),
            isEdit: true,
          });
        });
        setNewRow([...rowArr]);
      }
    }
  }, [editPurchase, supplierForSearch, branchForSearch]);

  //on change input field
  const handleChange = (e) => {
    if(e.target.name === "paymentType"){
      const sanitizedText = e.target.value.replace(/[^A-Za-z]/g, '');
      setNewItem({ ...newItem, [e.target.name]: sanitizedText });
    }else{
      setNewItem({ ...newItem, [e.target.name]: e.target.value });
    }
  };

  const getStock = async (index,id)=>{
    const url = BASE_URL + "/settings/get-finished-good-stock/" + id + "/branch/" + newItem?.branch?.id;
    setDataPaginating(true)
    try {
      const res = await axios
        .get(url, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        setDataPaginating(false)
        if(res){
          newRow[index].actual_stock = res.data;
          if(newItem.branch?.max_qty_check_purchase){
            const maxLimit = ingredientLimitCheck?.fgs?.[newItem.branch?.id]?.[id]?.max_limit;
            if(maxLimit){
              newRow[index].max_qty = (maxLimit ?? 0) - (res.data ? Number(res.data) : 0);
            }
          }
          setNewRow([...newRow]); 
        }       
    } catch {
      setDataPaginating(false)
      toast.error(`${_t(t("Please try again"))}`, toastObj);
    }
  }

  //set items hook
  const handleSetItems = async (index,item) => {
    newRow[index].unit="";
    newRow[index].base_unit="";
    newRow[index].base_qty="";
    newRow[index].conversion_qty="";
    newRow[index].item_name=item;
    newRow[index].base_unit=item.unit_id;
    newRow[index].item_units="";
    newRow[index].rate= Number(item.cost_price) || 0;
    newRow[index].actual_stock = "";
    newRow[index].max_qty = "";
    if(item?.finished_good_stock || newItem.branch?.max_qty_check_purchase){
      getStock(index,item.id);
    }else{
      setNewRow([...newRow]);
    }
  };

  //set image hook
  const handleItemImage = (e) => {
    setNewItem({
      ...newItem,
      [e.target.name]: e.target.files[0],
    });
    if (document.getElementById([e.target.name])) {
      document.getElementById([e.target.name]).value = '';
    }
  };

  const handleSetUnit=(index,unit)=>{
    newRow[index].unit = unit;
    newRow[index].conversion_qty = roundOffStock(unit.qty);
    setNewRow([...newRow]);
  }

  const calBaseQty=(index)=>{
    let qty=newRow[index].qty;
    let conversion_qty=newRow[index].conversion_qty
    if(qty && conversion_qty){
      return roundOffStock(qty * conversion_qty);
    }else{
      return "";
    }
  }

  const calBasePrice=(index)=>{
    let amount = handleGetAmount(index);
    let baseQty = calBaseQty(index);
    if(amount && baseQty){
      return roundOffStock(amount/baseQty);
    }else{
      return 0;
    }
  }

  const addNewRow=()=>{  
    if(!newItem?.branch){
      toast.error("Please select branch first");
      return
    }
    if(dataPaginating){
      toast.error("Please wait loading...");
      return
    }
    setNewRow((pre)=>[...pre,{
      item_name:"",
      ref_doc_identity: "",
      qty:"",
      unit:"",
      item_units:"",
      rate:"",
      amount:"",
      discount_percent:"",
      discount:0,
      tax_percent:"",
      tax:0, 
      advance_tax_percent:"",
      advance_tax:0,
      total:"",
      base_unit:"",
      base_qty:"",
      actual_stock: "",
      max_qty: "",
      conversion_qty:""
    }])
  }

  const delRow=(id)=>{
    if(dataPaginating){
      toast.error("Please wait loading...");
      return
    }
    let row=[...newRow]
    row.splice(id, 1);
    setNewRow(row);
  }

  // discount
  const handleDiscountItem = (index) => {
    let discount = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }

    if (newRow !== null && newRow[index].discount_percent !== "") {
      discount = parseFloat(newRow[index].discount_percent);
    }
    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return roundOffStock(rate * qty * (discount / 100));
  };

  const handleDiscounPercent = (index) => {
    let discount = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }

    if (newRow !== null && newRow[index].discount !== "") {
      discount = parseFloat(newRow[index].discount);
    }
    return rate && qty ? roundOffStock((discount / (rate * qty)) * 100) : 0;
  };

  const handleTaxItem = (index) => {
    let tax = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }

    if (newRow !== null && newRow[index].tax_percent !== "") {
      tax = parseFloat(newRow[index].tax_percent);
    }

    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return roundOffStock(rate * qty * (tax / 100));
  };

  const handleTaxPercent = (index) => {
    let tax = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }

    if (newRow !== null && newRow[index].tax !== "") {
      tax = parseFloat(newRow[index].tax);
    }

    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return rate && qty ? roundOffStock((tax / (rate * qty)) * 100) : 0;
  };

  //subtotal
  const handleGetSubTotal = (index) => {
    let qty = 0;
    let rate = 0;
    let tax = 0;
    let discount = 0;

    let advance_tax = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    if (newRow !== null && (newRow[index]?.tax || newRow[index]?.tax_percent)) {
      tax = fgTaxFixed ? (Number(newRow[index].tax) || 0) : handleTaxItem(index);
    }
    if (newRow !== null && (newRow[index]?.advance_tax || newRow[index]?.advance_tax_percent)) {
      advance_tax = fgTaxFixed ? (Number(newRow[index].advance_tax) || 0) : handleAdvanceTaxItem(index);
    }
    if (newRow !== null && (newRow[index].discount_percent || newRow[index].discount)) {
      discount = fgDiscountFixed ? (Number(newRow[index].discount) || 0) : handleDiscountItem(index);
    }
    return roundOffStock(
      (rate * qty) + tax + advance_tax - discount
    );
  };

  const handleDisAmount = () => {
    let discount = 0;
    if (newRow !== null && newRow.length>0) {
      newRow.map((item) => {
        if(fgDiscountFixed){
          if(item.discount){
            discount = discount + Number(item.discount);
          }
        }else{
          if (item.discount_percent) {
            discount = discount +
              item.rate *
                item.qty *
                (item.discount_percent / 100);
          }
        }
      });
    }    
    return roundOffStock(discount);
  };

  const handleTaxAmount = () => {
    let tax = 0;
    if (newRow !== null && newRow.length>0) {
      newRow.map((item) => {
        if(fgTaxFixed){
          if(item.tax){
            tax = tax + Number(item.tax);
          }
        }else{
          if (item.tax_percent) {
            tax = tax +
              item.rate * item.qty *
              (item.tax_percent / 100);
          }
        }
      });
    }
    return roundOffStock(tax);
  };

  const handleAdvanceTaxItem = (index) => {
    let advance_tax = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }

    if (newRow !== null && newRow[index].advance_tax_percent !== "") {
      advance_tax = parseFloat(newRow[index].advance_tax_percent);
    }

    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return roundOffStock(rate * qty * (advance_tax / 100));
  };

  const handleAdvanceTaxPercent = (index) => {
    let advance_tax = 0;
    let rate = 0;
    let qty = 0;

    if (newRow !== null && newRow[index].qty) {
      qty = parseFloat(newRow[index].qty);
    }

    if (newRow !== null && newRow[index].advance_tax !== "") {
      advance_tax = parseFloat(newRow[index].advance_tax);
    }

    if (newRow !== null && newRow[index].rate  !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return rate && qty ? roundOffStock((advance_tax / (rate * qty)) * 100) : 0;
  };

  const handleAdvanceTaxAmount = () => {
    let advance_tax = 0;
    if (newRow !== null && newRow.length>0) {
      newRow.map((item) => {
        if(fgTaxFixed){
          if(item.advance_tax){
            advance_tax = advance_tax + Number(item.advance_tax);
          }
        }else{
          if (item.advance_tax_percent) {
            advance_tax = advance_tax +
              item.rate * item.qty *
              (item.advance_tax_percent / 100);
          }
        }
      });
    }
    return roundOffStock(advance_tax);
  };

  // total amount
  const handleAmount = () => {
    let total = 0;
    if (newRow !== null && newRow.length>0) {
      newRow.map((item) => {        
        if (
          parseFloat(item.qty) && parseFloat(item.rate)
        ) {
          total = total +  parseFloat(item.qty) * parseFloat(item.rate);
        }
      });
    }
    return roundOffStock(total);
  };

  //total
  const handleGetTotal = () => {
    let total = 0;
    let tax = 0;
    let advance_tax = 0;
    let discount = 0;
    if (newRow !== null && newRow.length>0) {
      newRow.map((item) => {
        tax = 0;
        advance_tax = 0;
        discount = 0;
        if(fgTaxFixed){
          if(item.tax){
            tax = Number(item.tax);
          }
        }else{
          if (item.tax_percent) {
            tax = item.rate *
              item.qty *
              (item.tax_percent / 100);
          }
        }
        if(fgDiscountFixed){
          if (item.discount) {
            discount = Number(item.discount);
          }
        }else{
          if (item.discount_percent) {
            discount = item.rate *
            item.qty * (item.discount_percent / 100);
          }
        }
        if(fgTaxFixed){
          if(item.advance_tax){
            advance_tax = Number(item.advance_tax);
          }
        }else{
          if (item.advance_tax_percent) {
            advance_tax = item.rate * item.qty * (item.advance_tax_percent / 100);
          }
        }
        if (
          parseFloat(item.qty) && parseFloat(item.rate)
        ) {
          total = total + (item.qty * item.rate) + tax + advance_tax - discount;
        }
      });
    }
    return roundOffStock(total);
  };

  //subtotal
  const handleGetAmount = (index) => {
    let qty = 0;
    let rate = 0;
    if (newRow !== null && newRow[index].qty !== "") {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow !== null && newRow[index].rate !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return roundOffStock(rate * qty);
  };

  //set each item qty
  const handleItemQty = (index,e) => {
    newRow[index].qty=e.target.value;
    setNewRow([...newRow]);
  };

  //set each item rate
  const handleItemRate = (index,e) => {
    newRow[index].rate=e.target.value;
    setNewRow([...newRow]);
  };

  //discount and tax
  const handleSetRow = (index,e, key) => {
    newRow[index][key]=e.target.value;
    setNewRow([...newRow]);
  };

  //handle Set branch hook
  const handleSetBranch = (branch) => {
    setNewItem({ ...newItem, branch });
    setNewRow([])
  };

  //get supplier items
  const getIngredientSupplier = async(id) =>{
    const url = BASE_URL + "/settings/edit-supplier/" + id
    try{
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      return res.data;
    }catch(err){
      toast.error(
        `${_t(t("Failed to fetch supplier items"))}`,{
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  }

  //handle Set supplier hook
  const handleSetSupplier = async(supplier) => {

    setNewItem({ ...newItem, supplier, ref_doc_identity: null});

    const supplierItems = await getIngredientSupplier(supplier?.id);

    if(supplierItems?.food_items && supplierItems.food_items?.length){

      const items = supplierItems.food_items.map((item,index)=>{
        return {
          item_name: item,
          ref_doc_identity: "",
          qty:"",
          unit:item.default_unit ?? "",
          rate: Number(item?.cost_price) || "", 
          amount:"",         
          discount_percent:"",
          discount:0,
          base_unit: item?.unit_id,
          tax_percent:"",
          tax:0, 
          advance_tax_percent:"",
          advance_tax:0,
          total:"",
          base_qty:"",
          conversion_qty:item.default_unit?.qty ?? ""
        }
      });
      setNewRow([...items]);
    }else if(newRow?.length){
      setNewRow([]);
    }
  };

  //post req of food item add
  const fgPurchaseAxios = async () => {
    const toastConfig = {
      position: "bottom-center",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      className: "text-center toast-notification",
    };

    // Format date
    const date = `${purchaseDate.getFullYear()}-${purchaseDate.getMonth() + 1}-${purchaseDate.getDate()}`;
    if (!validateItemsProperty(newRow, "rate", _t(t("Price cannot be zero or empty")))) {
      return;
    }
    // Build items array - filter out empty items
    let isStock = false;
    let duplicateObj = {};
    const duplicateItem = [];
    const items = newRow
      .filter(item => {
        const isFgStock = item?.isEdit ? item?.finished_good_stock : item.item_name?.finished_good_stock;
        if(isFgStock && (item?.actual_stock > 0)){
          isStock = true
        }
        return item.item_name && item.item_name !== "" && (isFgStock ? (!item?.actual_stock || item?.actual_stock <= 0) : true)
      })
      .map((item, index) => {
        const itemId = item.isEdit ? item.item_id : item.item_name.id;
        const itemName = item.isEdit ? item.item_name : item.item_name.name;
        if(duplicateObj[itemId]){
          duplicateItem.push({
            item_id: itemId,
            item_name: itemName,  
          })
        }
        duplicateObj[itemId] = true;
        return {
          item_id: itemId,
          item_name: itemName,
          qty: parseFloat(item.qty),
          rate: parseFloat(item.rate),
          amount: handleGetAmount(index),
          discount: fgDiscountFixed ? (item.discount || 0) : handleDiscountItem(index),
          discount_percent: fgDiscountFixed ? handleDiscounPercent(index) : parseFloat(item.discount_percent || 0),
          tax: fgTaxFixed ? (item.tax || 0) : handleTaxItem(index),
          tax_percent: fgTaxFixed ? handleTaxPercent(index) : parseFloat(item.tax_percent || 0),
          advance_tax_percent: fgTaxFixed ? handleAdvanceTaxPercent(index) : parseFloat(item.advance_tax_percent || 0),
          advance_tax: fgTaxFixed ? (item.advance_tax || 0) : handleAdvanceTaxItem(index),
          total: handleGetSubTotal(index),
        }
      });
    if(duplicateItem?.length){
      toast.error(`${_t(t(`Duplicate item ${duplicateItem[0].item_name} found`))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
      return;
    }

    if(isStock){
      toast.error(`${_t(t("Please check item stock"))}`, toastConfig);
      return;
    }

    // Validate all items are complete
    if (items.length !== newRow.length) {
      toast.error(`${_t(t("Please select all dropdowns"))}`, toastConfig);
      return;
    }

    // Prepare payload object
    const payload = {
      invoice_number: newItem.invoice,
      ...(params.id && !duplicate && { purchase_id: params.id }),
      purchase_date: date,
      branch_id: newItem.branch.id,
      supplier_id: newItem.supplier.id,
      supplier_name: newItem.supplier.name,
      fgp_document: newItem.document || null,
      ref_doc_identity: newItem.ref_doc_identity?.document_identity || "",
      manual_invoice_no: newItem.manual_invoice_no,
      description: newItem.description,
      payment_type: newItem.paymentType,
      total_amount: handleAmount(),
      total_discount: handleDisAmount(),
      total_tax: handleTaxAmount(),
      total_advance_tax: handleAdvanceTaxAmount(),
      total_bill: handleGetTotal(),
      net_amount: handleGetTotal() + (Number(newItem.transportation) || 0),
      transportation: newItem.transportation || 0,
      credit_limit_days: newItem.credit_limit_days || "",
      items: items,
    };
		
    // Submit data
    setLoading(true);
    try {
      let res;
      if (params.id && !duplicate) {
        // PUT method for update
        const url = BASE_URL + `/settings/finished-good-purchase/${params.id}`;
        res = await axios.put(url, payload, {
          headers: { 
            Authorization: `Bearer ${getCookie()}`,
            'Content-Type': 'application/json'
          },
        });
      } else {
        // POST method for create/duplicate
        const url = BASE_URL + "/settings/finished-good-purchase";
        res = await axios.post(url, payload, {
          headers: { 
            Authorization: `Bearer ${getCookie()}`,
            'Content-Type': 'application/json'
          },
        });
      }
      
      if (res.data) {
        const successMsg = duplicate || !params.id 
          ? "FG Purchase has been added" 
          : "FG Purchase has been updated";
				ToastSuccess({ title: `${_t(t(successMsg))}` });
        history.push("/dashboard/manage/stock/fg-purchase");
      }
    } catch (error) {
      console.error("FG Purchase error:", error);
      if(error.response.data?.status === "error"){
        ToastError({ title: `${_t(t(error.response.data.message))}` });
        return;
      }
			ToastError({ title: `${_t(t("Please try again later"))}` });
    } finally {
      setLoading(false);
    }
  };
  
  //send to server
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const toastConfig = {
      position: "bottom-center",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      className: "text-center toast-notification",
    };

    // Validate required fields
    const isValidForm = newItem.branch && 
                       newItem.supplier && 
                       newItem.paymentType && 
                       purchaseDate;

    if (!isValidForm) {
      toast.error(`${_t(t("Please fill all the required fields"))}`, toastConfig);
      return;
    }

    if (newRow.length === 0) {
      toast.error(`${_t(t("Please add items"))}`, toastConfig);
      return;
    }

    fgPurchaseAxios();
  };

  return (
    <>
      <Helmet>
        <title>{_t(t(`${duplicate ? "Add FG Purchase" : params.id ? "Edit FG Purchase" : "Add FG Purchase"}`))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* next page data spin loading */}
                    <div className={`${dataPaginating && "loading"}`}></div>
                    {/* spin loading ends */}

                    {/* Loading effect */}
                    {loading === true ? (
                      tableLoading()
                    ) : (
                      <div key="smtp-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {_t(t(`${duplicate ? "Add FG Purchase" : params.id ? "Edit FG Purchase" : "Add FG Purchase"}`))}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        {/* Form starts here */}
                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                           
                            <div className="row">
                              <div className="form-group col-3 mt-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="invoice"
                                    className="control-label"
                                  >
                                    {_t(t("Purchase No"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    readOnly
                                    type="text"
                                    className="form-control table-text"
                                    id="invoice"
                                    name="invoice"
                                    onChange={handleChange}
                                    value={newItem.invoice}
                                    placeholder="Auto no"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="form-group col-3 text-right mt-3">
                                <div className="mb-2 text-left">
                                  <label
                                    htmlFor="purchaseDate"
                                    className="control-label"
                                  >
                                    {_t(t("Purchase Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    minDate={piBackDate && authUserInfo?.details?.user_type !== "superAdmin" && new Date()}
                                    selected={purchaseDate}
                                    onChange={(date) => setPurchaseDate(date)}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Purchase date"))}
                                    shouldCloseOnSelect={false}
                                  />
                                </div>
                              </div>

                              <div className="form-group mt-3 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="paymentType"
                                    className="control-label"
                                  >
                                    {_t(t("Payment Type"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div
                                  className="mb-2 d-flex align-items-center"
                                  style={{ height: "2rem"}}
                                >
                                  <div className="col-6 d-flex align-items-center">
                                    <input
                                      style={{fontSize:"1.2rem"}}
                                      type="radio"
                                      className="form-check-input pointer-cursor table-text"
                                      id="cash"
                                      name="paymentType"
                                      value="cash"
                                      onChange={(e) => {
                                        handleChange(e);
                                      }}
                                      checked={
                                        newItem.paymentType === "cash" ? 1 : 0
                                      }
                                    />
                                    <label
                                      className="form-radio-label pointer-cursor ml-2 mt-2"
                                      htmlFor="cash"
                                      style={{fontSize:"0.9rem"}}
                                    >
                                      {_t(t("Cash"))}
                                    </label>
                                  </div>
                                  <div className="col-6 d-flex align-items-center">
                                    <input
                                      style={{fontSize:"1.2rem"}}
                                      type="radio"
                                      className="form-check-input pointer-cursor table-text"
                                      id="credit"
                                      name="paymentType"
                                      value="credit"
                                      onChange={(e) => {
                                        handleChange(e);
                                      }}
                                      checked={
                                        newItem.paymentType === "credit" ? 1 : 0
                                      }
                                    />
                                    <label
                                      className="form-radio-label pointer-cursor ml-2 mt-2"
                                      htmlFor="credit"
                                      style={{fontSize:"0.9rem"}}
                                    >
                                      {_t(t("Credit"))}
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div className="form-group col-3 text-right mt-3">
                                
                              </div>     
                            </div>

                            <div className="row">                              
                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="branch"
                                    className="control-label"
                                  >
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>                                      
                                  </label>
                                </div>
                                <Select
                                  options={branchForSearch || []}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={newItem.branch || ""}
                                  classNamePrefix="select"
                                  onChange={handleSetBranch}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={
                                    _t(t("Please select a branch")) + ".."
                                  }
                                />
                              </div>  

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="supplier"
                                    className="control-label"
                                  >
                                    {_t(t("Supplier"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={supplierForSearch}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={newItem.supplier || ""}
                                  classNamePrefix="select"
                                  onChange={handleSetSupplier}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={
                                    _t(t("Please select a supplier")) + ".."
                                  }
                                />
                              </div>  

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="manual_inv_no"
                                    className="control-label"
                                  >
                                    {_t(t("Manual Purchase No"))}
                                  </label>
                                </div>

                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control table-text"
                                    id="manual_inv_no"
                                    name="manual_invoice_no"
                                    onChange={handleChange}
                                    value={newItem.manual_invoice_no}
                                    placeholder="Purchase no"
                                  />
                                </div> 
                              </div> 

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="credit_limit_days"
                                    className="control-label"
                                  >
                                    {_t(t("Credit Limit Days"))}
                                  </label>

                                </div>
                                <div className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control table-text"
                                    id="credit_limit_days"
                                    name="credit_limit_days"
                                    onChange={handleChange}
                                    value={newItem.credit_limit_days}
                                    placeholder="Credit limit days"
                                  />
                                </div> 
                              </div>
                              
                            </div> 
                            
                            <div className="row">

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="doc"
                                    className="form-label d-block"
                                  >
                                    {_t(t("Attach Document"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    accept="image/*,.pdf,.jpg,.jpeg,.png,.mp4"
                                    type="file"
                                    className="form-control sm-text d-none"
                                    id="doc"
                                    name="document"
                                    onChange={handleItemImage}
                                  />
                                  <label htmlFor="doc" className="image-btn">
                                    Choose File
                                  </label>
                                  {newItem?.document && (
                                    <>
                                      <span className="text-truncate d-inline-block" style={{maxWidth:"5rem", marginBottom:"-0.35rem"}}>
                                        {newItem?.document?.name}
                                      </span>
                                      <button type="button" onClick={(e)=>setNewItem({...newItem, document: ""})} className="btn-close ml-2 cursor-pointer" aria-label="Close"></button>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="form-group mt-2 col-9">
                                <div className="mb-2">
                                  <label
                                    htmlFor="description"
                                    className="control-label"
                                  >
                                    {_t(t("Description"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <textarea
                                    type="text"
                                    className="form-control table-text"
                                    id="description"
                                    name="description"
                                    onChange={handleChange}
                                    value={newItem.description}
                                    placeholder="Short description"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className={`table-responsive mt-3 pr-1 `}
                              style={{maxHeight:"23rem",overflow: "auto"}}>
                                <table className="table table-bordered-sm table-hover">
                                  <thead className="align-middle">                                    
                                    <tr>
                                    {newRow.length==0 ?<th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span className="t-mr-10" onClick={()=>addNewRow()}>
                                        <i className="fa fa-plus-circle cursor-pointer btn-add-icon" style={{fontSize:"22px"}} aria-hidden="true"></i>
                                      </span>
                                    </th>:<th style={{borderBottomColor:"lightgray"}}></th>}
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border table-Child"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Item Name"))}
                                        </span>
                                      </th> 
                                
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Stock Qty"))}
                                        </span>
                                      </th>

                                      {
                                        newItem.branch?.max_qty_check_purchase ? (
                                          <th
                                            scope="col"
                                            className="table-text text-capitalize align-middle text-center border-1 border"
                                          >
                                            <span style={{fontWeight:500}}>
                                            {_t(t("Max Qty"))}
                                            </span>
                                          </th>
                                        ) : ""
                                      }
                                      
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Qty"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Rate"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Amount"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                        style={{minWidth:'130px'}}
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Discount Percent"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                        style={{minWidth:"150px"}}
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Discount Amount"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Tax Percent"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Tax Amount"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Advance Tax Percent"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Advance Tax Amount"))}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Total"))}
                                        </span>
                                      </th>
                                      
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border d-none"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Base unit"))}
                                        </span>
                                      </th>
                                      
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Base qty"))}
                                        </span>
                                      </th>
                                      
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                        style={{minWidth:"9rem"}}
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Conversion unit"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                        style={{minWidth:"9rem"}}
                                      >
                                        <span style={{fontWeight:500}}>
                                        {_t(t("Base price"))}
                                        </span>
                                      </th>
                                      {newRow.length>0 && <th style={{borderBottomColor:"lightgray",minWidth:"60px"}}></th>}
                                    </tr>
                                  </thead>
                                  {newRow && newRow.length>0 ?
                                  <tbody className="align-middle">
                                    {newRow.map(
                                      (foodItem, index) => {
                                        return (
                                          <tr
                                            className="align-middle"
                                            key={index}
                                          >
                                            <td className="table-text text-capitalize align-middle text-center table-firstChild" style={{minWidth:"60px"}}>
                                              {index==(newRow.length-1) && <span className="t-mr-10" onClick={()=>addNewRow()}>
                                              <i className="fa fa-plus-circle btn-add-icon" style={{fontSize:"22px"}} aria-hidden="true"></i>
                                              </span>}&nbsp;
                                              <i className="fa fa-minus-circle cursor-pointer text-danger" style={{fontSize:"22px"}} 
                                              onClick={()=>{delRow(index)}}
                                              ></i>
                                            </td>
                                            <td className="table-text text-capitalize align-middle text-center">
                                            {foodItem.isEdit ?
                                              <input
                                              readOnly
                                              type="text"
                                              className={`form-control text-center table-Input table-text`}
                                              value={foodItem.item_name || ""}
                                              />
                                              : 
                                              <Select
                                                options={foodForPOS !== null ? foodForPOS : []}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                classNamePrefix="select"
                                                onChange={(e)=>handleSetItems(index,e)}
                                                maxMenuHeight="12.8rem"                                                
                                                value={foodItem.item_name}
                                                styles={customStyle}
                                                menuPlacement={index >= 1 ? "top" : "bottom"} 
                                                isMenuOpen={true}
                                                menuPosition="fixed"
                                                className="bank-table-input"                                              
                                                placeholder={
                                                _t(t("Please select items")) + ".."
                                                }                                                
                                              />
                                            }
                                            </td>

                                            <td className="table-text text-capitalize align-middle text-center">
                                              {foodItem?.actual_stock ?? ""}
                                            </td>

                                            {
                                              newItem.branch?.max_qty_check_purchase ? (
                                                <td className="table-text text-capitalize align-middle text-center">
                                                  {foodItem?.max_qty < 0 ? 0 : (foodItem?.max_qty ?? "")}
                                                </td>
                                              ) : ""
                                            }

                                            <td className="table-text text-capitalize align-middle text-center">
                                              <input
                                                type="number"
                                                step="1"
                                                min="0"
                                                onKeyDown={(evt) =>
                                                  ["e", "E", "+", "-", ".", "ArrowUp","ArrowDown"].includes(evt.key) &&
                                                  evt.preventDefault()
                                                }
                                                max={foodItem?.max_qty ? foodItem?.max_qty : undefined}
                                                onWheel={(e) => e.target.blur()}
                                                onInput={(e) => {
                                                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                }}
                                                className="form-control text-center table-Input table-text"
                                                id={foodItem.id}
                                                name={foodItem.id}
                                                onChange={(e)=>handleItemQty(index,e)}
                                                value={                                                   
                                                  foodItem.qty || ""
                                                }
                                                placeholder="Qty"
                                                required                                                  
                                              />
                                            </td>

                                            <td className="table-text text-capitalize align-middle text-center">
                                              <input
                                                type="number"
                                                step={qtyAmountPoints()}
                                                min="0"
                                                onKeyDown={(evt) =>
                                                  ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                  evt.preventDefault()
                                                }
                                                onWheel={(e) => e.target.blur()}
                                                className="form-control text-center table-Input table-text"
                                                id={foodItem.id}
                                                name={foodItem.id}
                                                onChange={(e)=>handleItemRate(index,e)}
                                                value={                                                    
                                                  foodItem.rate || ""
                                                }
                                                placeholder="Price"
                                                required                                                  
                                              />
                                            </td>
                                            <td className="table-text align-middle text-center">
                                              {formatPrice(
                                                handleGetAmount(                                                    
                                                  index
                                                )
                                              )}
                                            </td>
                                            
                                            {
                                              fgDiscountFixed ? (
                                                <>
                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    {formatPrice(
                                                      handleDiscounPercent(                                                    
                                                        index
                                                      )
                                                    )}
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={foodItem.id}
                                                      name={foodItem.id}
                                                      onChange={(e)=>handleSetRow(index,e,"discount")}
                                                      value={                                                    
                                                        foodItem.discount || ""
                                                      }
                                                      placeholder="Discount"
                                                    />
                                                  </td>
                                                </>
                                              ) : (
                                                <>
                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={foodItem.id}
                                                      name={foodItem.id}
                                                      onChange={(e)=>handleSetRow(index,e,"discount_percent")}
                                                      value={                                                    
                                                        foodItem.discount_percent || ""
                                                      }
                                                      placeholder="Discount %"
                                                    />
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    {formatPrice(
                                                      handleDiscountItem(                                                    
                                                        index
                                                      )
                                                    )}
                                                  </td>
                                                </>
                                              )
                                            }

                                            {
                                              fgTaxFixed ? (
                                                <>
                                                <td className="table-text text-capitalize align-middle text-center">
                                                  {
                                                    formatPrice(handleTaxPercent(index))
                                                  }
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={foodItem.id}
                                                      name={foodItem.id}
                                                      onChange={(e)=>handleSetRow(index,e,"tax")}
                                                      value={                                                    
                                                        foodItem.tax || ""
                                                      }
                                                      placeholder="Tax"
                                                    />
                                                  </td>
                                                </>
                                              ) : (
                                                <>
                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={foodItem.id}
                                                      name={foodItem.id}
                                                      onChange={(e)=>handleSetRow(index,e,"tax_percent")}
                                                      value={                                                    
                                                        foodItem.tax_percent || ""
                                                      }
                                                      placeholder="Tax"
                                                    />
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    {formatPrice(
                                                      handleTaxItem(                                                    
                                                        index
                                                      )
                                                    )}
                                                  </td>
                                                </>
                                              )
                                            }

                                            {
                                              fgTaxFixed ? (
                                                <>
                                                <td className="table-text text-capitalize align-middle text-center">
                                                  {
                                                    formatPrice(handleAdvanceTaxPercent(index))
                                                  }
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={`adv_tax_${index}`}
                                                      name={`adv_tax_${index}`}
                                                      onChange={(e)=>handleSetRow(index,e,"advance_tax")}
                                                      value={                                                    
                                                        foodItem.advance_tax || ""
                                                      }
                                                      placeholder="Adv Tax"
                                                    />
                                                  </td>
                                                </>
                                              ) : (
                                                <>
                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    <input
                                                      type="number"
                                                      step={qtyAmountPoints()}
                                                      min="0"
                                                      onKeyDown={(evt) =>
                                                        ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                                        evt.preventDefault()
                                                      }
                                                      onWheel={(e) => e.target.blur()}
                                                      className={`form-control text-center table-Input table-text ${                                                    
                                                        ""
                                                      }`}
                                                      id={`adv_tax_percent_${index}`}
                                                      name={`adv_tax_percent_${index}`}
                                                      onChange={(e)=>handleSetRow(index,e,"advance_tax_percent")}
                                                      value={                                                    
                                                        foodItem.advance_tax_percent || ""
                                                      }
                                                      placeholder="Adv Tax"
                                                    />
                                                  </td>

                                                  <td className="table-text text-capitalize align-middle text-center">
                                                    {formatPrice(
                                                      handleAdvanceTaxItem(                                                    
                                                        index
                                                      )
                                                    )}
                                                  </td>
                                                </>
                                              )
                                            }

                                            <td className="table-text text-capitalize align-middle text-center">
                                              {formatPrice(
                                                handleGetSubTotal(                                                    
                                                  index
                                                )
                                              )}
                                            </td>

                                            {/* hidden columns */}
                                            
                                            <td className="table-text align-middle text-center d-none">
                                              {foodItem.base_unit}
                                            </td> 
                                            
                                            <td className="table-text align-middle text-center">                                                
                                              {formatPrice(calBaseQty(index))}
                                            </td>
                                            
                                            <td className="table-text align-middle text-center">
                                              {foodItem.conversion_qty}
                                            </td> 

                                            <td className="table-text align-middle text-center">
                                              {formatPrice(calBasePrice(index))}
                                            </td> 

                                            <td className="table-text text-capitalize align-middle text-center" style={{minWidth:"60px"}}>
                                              <i className="fa fa-minus-circle cursor-pointer text-danger" style={{fontSize:"22px"}} onClick={()=>{delRow(index)}}></i>&nbsp;
                                              {index==(newRow.length-1) && <span className="t-mr-10" onClick={()=>addNewRow()}>
                                                <i className="fa fa-plus-circle cursor-pointer btn-add-icon" style={{fontSize:"22px"}} aria-hidden="true"></i>
                                              </span>}                                             
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )}
                                  </tbody>
                                  :null}
                                  {newRow && newRow.length>0 ?
                                  <tfoot className="align-middle">
                                    <tr>
                                      <th
                                        scope="col"
                                        colSpan="1"
                                        className="sm-text text-capitalize align-middle text-center border-1 border"
                                      ></th>

                                      <th
                                        scope="col"
                                        colSpan="3"
                                        className="sm-text text-capitalize align-middle text-center border-1 border"
                                      ></th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {_t(t("Total"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {formatPrice(handleAmount())}
                                        </span>
                                      </th>
                                      <th                                      
                                      scope="col"                                        
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {formatPrice(handleDisAmount())}
                                        </span>
                                      </th>
                                      <th                                      
                                      scope="col"                                        
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {formatPrice(handleTaxAmount())}
                                        </span>
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                      </th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {formatPrice(handleAdvanceTaxAmount())}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>                                            
                                          {formatPrice(handleGetTotal())}
                                        </span>
                                      </th>

                                      <th                                      
                                        scope="col"                                        
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      
                                      <th                                      
                                        scope="col"                                        
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      
                                      <th                                      
                                        scope="col"                                        
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>
                                      
                                      <th                                      
                                        scope="col"                                        
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      ></th>

                                    </tr>
                                    <tr>
                                      <th colSpan="1"></th>
                                      <th colSpan="10"></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {_t(t("Transport"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <input
                                          type="number"
                                          step={qtyAmountPoints()}
                                          min="0"
                                          onKeyDown={(evt) =>
                                            ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                            evt.preventDefault()
                                          }
                                          onWheel={(e) => e.target.blur()}
                                          className="form-control text-center table-Input table-text"
                                          id="transportation"
                                          name="transportation"
                                          onChange={(e)=>handleChange(e)}
                                          value={                                                   
                                            newItem.transportation || ""
                                          }
                                        />
                                      </th>
                                      <th colSpan="3"></th>
                                      <th colSpan="1"></th>
                                    </tr>
                                    <tr>
                                      <th colSpan="1"></th>
                                      <th colSpan="10"></th>
                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>
                                          {_t(t("Net Total"))}
                                        </span>
                                      </th>

                                      <th
                                        scope="col"
                                        className="table-text text-capitalize align-middle text-center border-1 border"
                                      >
                                        <span style={{fontWeight:"bold"}}>                                            
                                          {formatPrice(handleGetTotal() + (Number(newItem.transportation) || 0))}
                                        </span>
                                      </th>
                                      <th colSpan="3"></th>
                                      <th colSpan="1"></th>
                                    </tr>
                                  </tfoot>
                                  :null}
                                </table>
                            </div>

                            <div className="form-group mt-4 pb-2">
                              <div className="d-flex justify-content-end align-items-center">
                                <div className="col-3 d-flex justify-content-end">
                                  <button
                                  type="button"
                                  className="btn rounded hov-btn mr-3"
                                  onClick={()=>(history.push("/dashboard/manage/stock/fg-purchase"))}
                                  >
                                    <i className="fa fa-arrow-left" aria-hidden="true"></i> {_t(t("Back"))}
                                  </button>
                                  <button
                                  disabled={loading}
                                  type="submit"
                                  className="btn btn-primary rounded"
                                  >
                                    <i className="fa fa-floppy-o" aria-hidden="true"></i> {_t(t(`${duplicate || !params.id ? "Save" : "Update"}`))}
                                  </button>                                                    
                                </div>                                                    
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}     
    </>
  );
};

export default FgInvoceAction;
